import { useEffect, useRef } from 'react'
import L from 'leaflet'
import type { Drone } from '../types'
import { getDroneColor } from '../utils'

import 'leaflet/dist/leaflet.css'

interface DroneMapProps {
  drones: Drone[]
}

const MAP_CENTER: [number, number] = [60.1808, 24.9582]
const SIMULATION_SCALE = 1
const NFZ_RADIUS_METERS = 1000 * SIMULATION_SCALE

export function DroneMap({ drones }: DroneMapProps) {
  const mapNodeRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markersRef = useRef<Record<string, L.Marker>>({})
  const animationRef = useRef<number | null>(null)
  const circleRef = useRef<L.Circle | null>(null)

  useEffect(() => {
    if (!mapNodeRef.current || mapRef.current) {
      return
    }

    // Dark tile layer (CartoDB Dark Matter)
    const darkLayer = L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        maxZoom: 19,
      }
    )

    const satelliteLayer = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Tiles &copy; Esri',
        maxZoom: 18,
      }
    )

    const map = L.map(mapNodeRef.current, {
      center: MAP_CENTER,
      zoom: 14,
      minZoom: 10,
      maxZoom: 19,
      zoomControl: true,
      attributionControl: false,
      scrollWheelZoom: true,
      doubleClickZoom: true,
      boxZoom: true,
      keyboard: true,
      dragging: true,
      touchZoom: true,
      zoomAnimation: true,
      fadeAnimation: true,
      layers: [darkLayer],
    })

    L.control.layers({ 'Dark': darkLayer, 'Satellite': satelliteLayer }).addTo(map)

    // NFZ circle with animated dashed border
    const nfzCircle = L.circle(MAP_CENTER, {
      radius: NFZ_RADIUS_METERS,
      color: '#ef4444',
      weight: 2,
      fillColor: '#ef4444',
      fillOpacity: 0.12,
      dashArray: '8, 12',
    }).addTo(map)

    mapRef.current = map
    circleRef.current = nfzCircle

    // Animate NFZ border
    let dashOffset = 0
    const animateCircle = () => {
      dashOffset -= 0.5
      const circlePath = (nfzCircle as L.Circle & { _path?: SVGPathElement })._path
      if (circlePath) {
        circlePath.style.strokeDashoffset = `${dashOffset}`
      }
      animationRef.current = window.requestAnimationFrame(animateCircle)
    }
    animateCircle()

    return () => {
      if (animationRef.current) {
        window.cancelAnimationFrame(animationRef.current)
      }
      Object.values(markersRef.current).forEach((marker) => marker.remove())
      markersRef.current = {}
      map.remove()
      mapRef.current = null
      circleRef.current = null
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const activeDroneIds = new Set<string>()
    const metersToLatDeg = 1 / 111_320
    const metersToLngDeg = 1 / (111_320 * Math.cos((MAP_CENTER[0] * Math.PI) / 180))

    drones.forEach((drone) => {
      const lat = MAP_CENTER[0] + drone.y * SIMULATION_SCALE * metersToLatDeg
      const lng = MAP_CENTER[1] + drone.x * SIMULATION_SCALE * metersToLngDeg
      const position: [number, number] = [lat, lng]
      const distance = Math.hypot(drone.x, drone.y)
      const color = getDroneColor(distance)
      const ownerLabel = String(drone.owner_id).slice(-2)

      activeDroneIds.add(drone.id)

      const icon = L.divIcon({
        html: `<div class="drone-marker drone-marker-${color}">${ownerLabel}</div>`,
        className: 'drone-icon-wrapper',
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      })

      const marker = markersRef.current[drone.id]

      if (marker) {
        marker.setLatLng(position)
        marker.setIcon(icon)
        return
      }

      markersRef.current[drone.id] = L.marker(position, { icon, zIndexOffset: 1000 }).addTo(map)
    })

    Object.keys(markersRef.current).forEach((id) => {
      if (!activeDroneIds.has(id)) {
        markersRef.current[id]?.remove()
        delete markersRef.current[id]
      }
    })
  }, [drones])

  return <div ref={mapNodeRef} className="drone-map" aria-label="Live drone map" />
}